import { supabase } from '@/integrations/supabase/client';

export interface DataRoom {
  id: string;
  name: string;
  description: string | null;
  status: 'draft' | 'active' | 'archived';
  owner_id: string;
  owner_email?: string;
  role?: string;
  member_count?: number;
  file_count?: number;
  created_at: string;
  updated_at?: string;
}

export interface DataRoomMember {
  id: string;
  user_id: string;
  email?: string;
  name?: string;
  role: 'viewer' | 'uploader' | 'data_room_admin';
  created_at: string;
}

export interface DataRoomFolder {
  id: string;
  name: string;
  parent_id: string | null;
  created_at: string;
}

export interface DataRoomFile {
  id: string;
  file_name: string;
  folder_id: string | null;
  storage_path: string;
  mime_type: string | null;
  size_bytes: number | null;
  uploaded_by: string;
  uploader_email?: string;
  uploaded_at: string;
}

export interface DataRoomDetails {
  data_room: DataRoom;
  role: string;
  can_upload: boolean;
  can_manage: boolean;
  members: DataRoomMember[];
  folders: DataRoomFolder[];
  files: DataRoomFile[];
}

// Check if user has 2FA enabled
export async function check2FAStatus(): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;

  const { data, error } = await supabase
    .from('user_profiles')
    .select('totp_enabled')
    .eq('user_id', user.id)
    .single();

  if (error || !data) return false;
  return data.totp_enabled === true;
}

// Get all data rooms for current user
export async function getUserDataRooms(): Promise<DataRoom[]> {
  const { data, error } = await supabase.rpc('get_user_data_rooms');
  
  if (error) {
    console.error('Error fetching data rooms:', error);
    throw error;
  }

  if (!data?.success) {
    throw new Error(data?.error || 'Failed to fetch data rooms');
  }

  return data.data_rooms || [];
}

// Get data room details
export async function getDataRoomDetails(dataRoomId: string): Promise<DataRoomDetails> {
  const { data, error } = await supabase.rpc('get_data_room_details', {
    p_data_room_id: dataRoomId
  });

  if (error) {
    console.error('Error fetching data room details:', error);
    throw error;
  }

  if (!data?.success) {
    throw new Error(data?.error || 'Failed to fetch data room details');
  }

  return {
    data_room: data.data_room,
    role: data.role,
    can_upload: data.can_upload,
    can_manage: data.can_manage,
    members: data.members || [],
    folders: data.folders || [],
    files: data.files || []
  };
}

// Create a new data room
export async function createDataRoom(name: string, description?: string): Promise<DataRoom> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('data_rooms')
    .insert({
      name,
      description,
      owner_id: user.id,
      status: 'draft'
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating data room:', error);
    throw error;
  }

  return data;
}

// Update data room
export async function updateDataRoom(
  dataRoomId: string, 
  updates: { name?: string; description?: string; status?: 'draft' | 'active' | 'archived' }
): Promise<DataRoom> {
  const { data, error } = await supabase
    .from('data_rooms')
    .update(updates)
    .eq('id', dataRoomId)
    .select()
    .single();

  if (error) {
    console.error('Error updating data room:', error);
    throw error;
  }

  return data;
}

// Add member to data room
export async function addDataRoomMember(
  dataRoomId: string,
  userEmail: string,
  role: 'viewer' | 'uploader' | 'data_room_admin'
): Promise<DataRoomMember> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // Find user by email
  const { data: profile, error: profileError } = await supabase
    .from('user_profiles')
    .select('user_id')
    .eq('email', userEmail)
    .single();

  if (profileError || !profile?.user_id) {
    throw new Error('User not found with that email');
  }

  const { data, error } = await supabase
    .from('data_room_members')
    .insert({
      data_room_id: dataRoomId,
      user_id: profile.user_id,
      role,
      invited_by: user.id
    })
    .select()
    .single();

  if (error) {
    console.error('Error adding member:', error);
    throw error;
  }

  return data;
}

// Update member role
export async function updateMemberRole(
  memberId: string,
  role: 'viewer' | 'uploader' | 'data_room_admin'
): Promise<void> {
  const { error } = await supabase
    .from('data_room_members')
    .update({ role })
    .eq('id', memberId);

  if (error) {
    console.error('Error updating member role:', error);
    throw error;
  }
}

// Remove member from data room
export async function removeMember(memberId: string): Promise<void> {
  const { error } = await supabase
    .from('data_room_members')
    .delete()
    .eq('id', memberId);

  if (error) {
    console.error('Error removing member:', error);
    throw error;
  }
}

// Create folder in data room
export async function createFolder(
  dataRoomId: string,
  name: string,
  parentId?: string
): Promise<DataRoomFolder> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('data_room_folders')
    .insert({
      data_room_id: dataRoomId,
      name,
      parent_id: parentId || null,
      created_by: user.id
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating folder:', error);
    throw error;
  }

  return data;
}

// Delete folder
export async function deleteFolder(folderId: string): Promise<void> {
  const { error } = await supabase
    .from('data_room_folders')
    .delete()
    .eq('id', folderId);

  if (error) {
    console.error('Error deleting folder:', error);
    throw error;
  }
}

// Upload file to data room
export async function uploadFile(
  dataRoomId: string,
  file: File,
  folderId?: string,
  relativePath?: string
): Promise<DataRoomFile> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // Create folder structure if needed
  let targetFolderId = folderId;
  if (relativePath) {
    const pathParts = relativePath.split('/').filter(p => p && p !== file.name);
    for (const folderName of pathParts) {
      const existingFolder = await findOrCreateFolder(dataRoomId, folderName, targetFolderId);
      targetFolderId = existingFolder.id;
    }
  }

  // Sanitize filename for storage (remove accents and problematic characters)
  const normalizeFileName = (name: string) => {
    // 1) Remove accents (é -> e, etc.)
    let base = name.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
    // 2) Replace non-breaking spaces and whitespace with underscores
    base = base.replace(/[\u00A0\s]+/g, '_');
    // 3) Remove characters that may be invalid in storage keys
    base = base.replace(/[^a-zA-Z0-9._-]/g, '_');
    // Avoid empty string
    return base || 'file';
  };

  const safeFileName = normalizeFileName(file.name);

  // Upload to storage
  const timestamp = Date.now();
  const storagePath = `${dataRoomId}/${targetFolderId || 'root'}/${timestamp}_${safeFileName}`;
  
  const { error: uploadError } = await supabase.storage
    .from('data-room-files')
    .upload(storagePath, file);

  if (uploadError) {
    console.error('Error uploading file:', uploadError);
    throw uploadError;
  }

  // Create file record
  const { data, error } = await supabase
    .from('data_room_files')
    .insert({
      data_room_id: dataRoomId,
      folder_id: targetFolderId || null,
      file_name: file.name,
      storage_path: storagePath,
      mime_type: file.type,
      size_bytes: file.size,
      uploaded_by: user.id
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating file record:', error);
    throw error;
  }

  return data;
}

// Helper to find or create folder - fixed to properly handle null parent_id
async function findOrCreateFolder(
  dataRoomId: string,
  name: string,
  parentId?: string
): Promise<DataRoomFolder> {
  // Build query properly for null parent_id
  let query = supabase
    .from('data_room_folders')
    .select('*')
    .eq('data_room_id', dataRoomId)
    .eq('name', name);
  
  if (parentId) {
    query = query.eq('parent_id', parentId);
  } else {
    query = query.is('parent_id', null);
  }
  
  const { data: existing } = await query.maybeSingle();

  if (existing) return existing;
  return createFolder(dataRoomId, name, parentId);
}

// Delete all folders in a data room (for cleanup)
export async function deleteAllFolders(dataRoomId: string): Promise<void> {
  const { error } = await supabase
    .from('data_room_folders')
    .delete()
    .eq('data_room_id', dataRoomId);

  if (error) {
    console.error('Error deleting folders:', error);
    throw error;
  }
}

// Delete all files in a data room (for cleanup)
export async function deleteAllFiles(dataRoomId: string): Promise<void> {
  // Get all files first
  const { data: files } = await supabase
    .from('data_room_files')
    .select('storage_path')
    .eq('data_room_id', dataRoomId);

  if (files && files.length > 0) {
    // Delete from storage
    const paths = files.map(f => f.storage_path);
    await supabase.storage.from('data-room-files').remove(paths);
  }

  // Delete records
  const { error } = await supabase
    .from('data_room_files')
    .delete()
    .eq('data_room_id', dataRoomId);

  if (error) {
    console.error('Error deleting files:', error);
    throw error;
  }
}

// Delete file
export async function deleteFile(fileId: string, storagePath: string): Promise<void> {
  // Delete from storage
  const { error: storageError } = await supabase.storage
    .from('data-room-files')
    .remove([storagePath]);

  if (storageError) {
    console.error('Error deleting from storage:', storageError);
  }

  // Delete record
  const { error } = await supabase
    .from('data_room_files')
    .delete()
    .eq('id', fileId);

  if (error) {
    console.error('Error deleting file record:', error);
    throw error;
  }
}

// Get file download URL
export async function getFileDownloadUrl(storagePath: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from('data-room-files')
    .createSignedUrl(storagePath, 3600); // 1 hour expiry

  if (error) {
    console.error('Error getting download URL:', error);
    throw error;
  }

  return data.signedUrl;
}

// Format file size
export function formatFileSize(bytes: number | null): string {
  if (!bytes) return '0 B';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
}

// Get file icon based on mime type
export function getFileIcon(mimeType: string | null): string {
  if (!mimeType) return '📄';
  
  // Images
  if (mimeType.startsWith('image/')) return '🖼️';
  
  // PDFs
  if (mimeType === 'application/pdf') return '📕';
  
  // Spreadsheets (Excel, Google Sheets, etc.)
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || 
      mimeType === 'application/vnd.ms-excel' ||
      mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') return '📊';
  
  // Documents (Word, Google Docs, etc.)
  if (mimeType.includes('word') || mimeType.includes('document') ||
      mimeType === 'application/msword' ||
      mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return '📝';
  
  // Presentations (PowerPoint, Google Slides, etc.)
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint') ||
      mimeType === 'application/vnd.ms-powerpoint' ||
      mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') return '📽️';
  
  // CSV and data files
  if (mimeType === 'text/csv') return '📈';
  if (mimeType === 'application/json') return '🔧';
  
  // Video files
  if (mimeType.startsWith('video/')) return '🎬';
  
  // Audio files
  if (mimeType.startsWith('audio/')) return '🎵';
  
  // Archive files
  if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('tar') ||
      mimeType.includes('7z') || mimeType.includes('archive')) return '📦';
  
  // Text files
  if (mimeType.startsWith('text/')) return '📄';
  
  return '📄';
}
